import { useIntl } from 'react-intl';
import { memo } from 'react';
import type { Segment } from '@library';
import { SegmentsPage } from '@library';
import type { ProductWithFixedRelatedProducts } from '../../util/processRelatedProducts';
import type { Product } from '../../types';
import { useProductMetaContent } from '../../queries/useProductMetaContent';
import { ProductBreadcrumbs } from '../ProductBreadcrumbs';
import styles from './ProductDetails.module.scss';
import ProductCompatibility from './ProductCompatibility';
import ProductDetailsDescription from './ProductDetailsDescription';
import ProductDetailsDownloads from './ProductDetailsDownloads';
import SpareParts from './ProductTables/SpareParts';
import ProductSummary from './ProductSummary';
import ProductBulkOrder from './ProductBulkOrder/ProductBulkOrder';
import ProductsCarousel from './ProductsCarousel';
import ProductHazardousInformation from './ProductHazardousInformation';

const processProduct = (product: Product, variantId?: string) => {
    const { id, variants, siblingAttributes, siblings } = product;

    const selectedSku =
        variants.find(variant => variant.id === variantId)?.sku ?? product.sku;

    const selectedVariant = variants.find(
        variant => variant.sku === selectedSku
    );

    const hasVariants = variants.length > 0;
    const productSiblings = [
        { id, siblingAttributes, variants },
        ...(siblings ?? []),
    ].sort((a, b) => a.id.localeCompare(b.id));

    const allProducts = [product, ...(siblings ?? [])].sort((a, b) =>
        a.id.localeCompare(b.id)
    );

    const showVariantsTable = hasVariants || productSiblings.length > 1;
    const selectedProduct = selectedVariant || product;

    return {
        selectedSku,
        selectedVariant,
        hasVariants,
        allProducts,
        showVariantsTable,
        selectedProduct,
    };
};

const ProductDetailsContent = ({
    product,
    variantId,
}: {
    product: ProductWithFixedRelatedProducts;
    variantId?: string;
}) => {
    const {
        selectedSku,
        selectedVariant,
        hasVariants,
        allProducts,
        showVariantsTable,
        selectedProduct,
    } = processProduct(product, variantId);

    const {
        accessories,
        spareParts,
        relatedProducts,
        alternativeProducts,
        vehicles,
        documents,
        replacementProducts,
        safetyDatasheetUrl,
        hazardStatements,
    } = product;

    const activeSku = hasVariants ? selectedVariant?.sku : product.sku;

    const metaContent = useProductMetaContent({
        product: product,
        activeSku,
    });

    const intl = useIntl();

    const segments: Segment[] = [
        {
            name: intl.formatMessage({ id: 'details' }),
            element: (
                <ProductSummary
                    product={product}
                    hasVariants={hasVariants}
                    selectedSku={selectedSku}
                    selectedVariant={selectedVariant}
                    replacementProducts={replacementProducts}
                />
            ),
            expandedStart: true,
        },
        {
            name: intl.formatMessage({ id: 'description & specifications' }),
            element: (
                <ProductDetailsDescription
                    description={selectedProduct.description}
                    specificationAttributes={
                        selectedProduct.specificationAttributes
                    }
                />
            ),
        },
        {
            name: intl.formatMessage({ id: 'product variants' }),
            element: <ProductBulkOrder products={allProducts} />,
            hide: allProducts.length <= 0 || !showVariantsTable,
        },
        {
            name: intl.formatMessage({ id: 'use' }),
            element: (
                <ProductCompatibility
                    vehicles={Array.isArray(vehicles) ? vehicles : []}
                />
            ),
            hide: (vehicles?.length ?? 0) <= 0,
        },
        {
            name: intl.formatMessage({
                id: 'Product ProductDetails Downloads',
            }),
            element: (
                <ProductDetailsDownloads
                    documents={documents}
                    safetyDatasheetUrl={safetyDatasheetUrl}
                />
            ),
        },
        {
            name: intl.formatMessage({ id: 'spare parts' }),
            element: <SpareParts spareParts={spareParts} />,
            hide: spareParts.length <= 0,
        },
        {
            name: intl.formatMessage({
                id: 'Product ProductDetails HazardousInformation',
            }),
            element: (
                <ProductHazardousInformation
                    hazardStatements={hazardStatements}
                />
            ),
            hide: !hazardStatements,
        },
        {
            name: intl.formatMessage({ id: 'accessories' }),
            element: <ProductsCarousel products={accessories} />,
            hide: accessories.length <= 0,
            testClass: 'relatedProducts',
        },
        {
            name: intl.formatMessage({ id: 'related articles' }),
            element: <ProductsCarousel products={relatedProducts} />,
            hide: relatedProducts.length <= 0,
            testClass: 'relatedProducts',
        },
        {
            name: intl.formatMessage({ id: 'similar articles' }),
            element: <ProductsCarousel products={alternativeProducts} />,
            hide: alternativeProducts.length <= 0,
            testClass: 'relatedProducts',
        },
    ];
    return (
        <div className={styles.productDetails}>
            <ProductBreadcrumbs description={metaContent} product={product} />
            <div className={styles.segments}>
                <SegmentsPage segments={segments} sticky />
            </div>
        </div>
    );
};

export default memo(ProductDetailsContent);
